/* eslint-disable react/jsx-no-useless-fragment */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import { ROUTE_PATH } from 'routes';
import { useNavigate, useParams } from 'react-router-dom';
import useLiterals from 'utils/hooks/useLiterals';
import {
  completeClientOnboarding,
  createIdMissionLog,
  generateSessionToken,
  getUserOtp,
  onLogin,
  postCompleteSignup,
  savePassword,
  searchClient,
  sendSignupOTP,
  validateSession,
  verifyClientAccountOTP,
  verifySignupOTP,
} from 'modules/session/actions';
import {
  buildCompleteClientOnboardingPayload,
  buildCompleteSignupPayload,
  buildIdMissionLogPayload,
  readCompleteOnboardingFieldErrors,
} from 'modules/session/buildCompleteSignupPayload';
import useLocalStorage from 'utils/hooks/useLocalStorage';
import {
  CLIENT_EXISTS_TYPES,
  getRegistrationProgress,
  getSignupOtpFieldToReset,
  isCompleteOtpRetryError,
  isSignupOtpLockedError,
  mapSignupOtpVerifyMessage,
  SIGNUP_OTP_CHANNEL,
  STEPS,
  SUCCESS_TYPES,
} from 'constants/registration';
import { STORAGE_USER_DATA } from 'constants/local';
import ErrorModal from 'components/FeedbackModals/ErrorModal';
import { LOGIN_TYPES, PHONE_COUNTRY_CODE, USER_LEVEL } from 'constants/global';
import Button from 'components/UI/Button';
import useFeatureGate from 'utils/hooks/useFeatureGate';
import { FEATURE_REGISTRATION, FEATURE_REGISTRATION_NEW_CLIENT } from 'constants/featureGates';
import { sendGaEvent } from 'utils/sendGaEvent';
import { GA_EVENTS, GA_REGISTRATION_ACTIONS, GA_REGISTRATION_LABELS } from 'constants/gaEvents';
import { sendSentryLog } from 'utils/sendSentryLog';
import { getPrefillReferralCode } from 'utils/getPrefillReferralCode';
import { openSupport } from 'utils/openSupport';
import ClientBasicInfo from './components/ClientBasicInfo';
import ClientOTPValidation from './components/ClientOTPValidation';
import ClientPassword from './components/ClientPassword';
import ClientIdentityValidation from './components/ClientIdentityValidation';
import ClientIdentityResume from './components/ClientIdentityResume';
import ClientAddress from './components/ClientAddress';
import ClientSuccessMessage from './components/ClientSuccessMessage';
import ClientStart from './components/ClientStart';
import ClientExistsOTPValidation from './components/ClientExistsOTPValidation';
import {
  getIdMissionLogTiming,
  ID_MISSION_LOG_TIMING,
  LEGACY_RESUME_STEPS,
  NEW_CLIENT_AUTH_STEP,
  REGISTRATION_MODE,
  REGISTRATION_REFRESH,
  resolveNewClientAuthStep,
  resolveRegistrationMode,
  resolveRegistrationRefresh,
} from './newClientAuthFlow';
import './styles.scss';

const ERROR_MESSAGES = {
  USER_ALREADY_EXISTS: 'USER_ALREADY_EXISTS',
  PHONE_ALREADY_EXISTS: 'PHONE_ALREADY_EXISTS',
  PHONE_ALREADY_EXISTS_IN_SAP: 'PHONE_ALREADY_EXISTS_IN_SAP',
  EMAIL_ALREADY_EXISTS: 'EMAIL_ALREADY_EXISTS',
  EMAIL_ALREADY_EXISTS_IN_SAP: 'EMAIL_ALREADY_EXISTS_IN_SAP',
  RFC_ALREADY_EXISTS: 'RFC_ALREADY_EXISTS',
  ERROR_CHECKING_EMAIL: 'ERROR_CHECKING_EMAIL',
  INVALID_PASSWORD_TOKEN: 'INVALID_PASSWORD_TOKEN',
  ERROR_SENDING_WHATSAPP: 'ERROR_SENDING_WHATSAPP',
};

const DUPLICATE_ACCOUNT_ERRORS = [
  ERROR_MESSAGES.PHONE_ALREADY_EXISTS,
  ERROR_MESSAGES.EMAIL_ALREADY_EXISTS,
];

const Registration = () => {
  const { step } = useParams();
  const navigate = useNavigate();
  const literals = useLiterals('registration');
  const errorLiterals = useLiterals('errors');
  const { user: sessionUser } = useSelector((state) => state.session);

  // Registration is available on every platform, but on desktop web only the
  // existing-client linking path is offered; new-client signup + KYC stays
  // mobile-only (WEB_MLS-608).
  const registrationEnabled = useFeatureGate(FEATURE_REGISTRATION);
  const newClientEnabled = useFeatureGate(FEATURE_REGISTRATION_NEW_CLIENT);
  useEffect(() => {
    if (!registrationEnabled) {
      navigate(ROUTE_PATH.HOME, { replace: true });
    }
  }, [registrationEnabled, navigate]);

  // Steps that belong to the new-client signup + KYC flow; unreachable when
  // that flow is disabled (desktop web).
  useEffect(() => {
    const newClientOnlySteps = [
      STEPS.BASIC_INFO,
      STEPS.OTP_VALIDATION,
      STEPS.IDENTITY_VALIDATION,
      STEPS.IDENTITY_RESUME,
      STEPS.ADDRESS,
    ];
    if (!newClientEnabled && newClientOnlySteps.includes(step)) {
      navigate(
        sessionUser?.id ? ROUTE_PATH.HOME : ROUTE_PATH.setRegistration(STEPS.START),
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, newClientEnabled]);

  const [error, setError] = useState(null);
  const [exists, setExists] = useState({ type: CLIENT_EXISTS_TYPES.CLIENT_ID, value: '', linked: false });
  const [errorEmailPassword, setErrorEmailPassword] = useState(false);
  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    paternal: '',
    maternal: '',
    gender: '',
    email: '',
    phone: '',
    referralCode: getPrefillReferralCode(),
    privacyTerms: false,
    appTerms: false,
  });
  const [otp, setOtp] = useState({ email: '', phone: '' });
  const [otpChannel, setOtpChannel] = useState(SIGNUP_OTP_CHANNEL.WHATSAPP);
  const [otpLocked, setOtpLocked] = useState({ phone: false, email: false });
  const [otpReset, setOtpReset] = useState(null);
  const [signupAuth, setSignupAuth] = useState(null);
  const [collectedAddress, setCollectedAddress] = useState(null);
  const [pendingCompleteRetry, setPendingCompleteRetry] = useState(false);
  const [legacyCompleteDone, setLegacyCompleteDone] = useState(false);
  const [clientOnboardingFieldErrors, setClientOnboardingFieldErrors] = useState({});
  const [duplicateAccount, setDuplicateAccount] = useState(null);
  const [newClientPasswordSaved, setNewClientPasswordSaved] = useState(false);
  const [smsFallbackSent, setSmsFallbackSent] = useState(false);
  const registrationMode = resolveRegistrationMode({
    userId: sessionUser?.id,
    userLevel: sessionUser?.level,
    legacyResumeEnabled: newClientEnabled,
  });
  const isLegacyResume = registrationMode === REGISTRATION_MODE.LEGACY_INCOMPLETE_RESUME;
  const [userExtra, setUserExtra] = useState({
    job: '',
    referral: '',
    educationLevel: '',
    countryOfBirth: '',
    citizenship: '',
  });
  const [passwordToken, setPasswordToken] = useState(null);
  const [userData, setUserData] = useLocalStorage(STORAGE_USER_DATA);
  const dispatch = useDispatch();

  const handleEvent = (type, label = '', nonInteraction = true) => {
    let auxLabel = label;
    if (typeof label === 'object') {
      if (typeof label?.data?.error === 'string') {
        auxLabel = label?.data?.error;
      } else {
        auxLabel = JSON.stringify(label).slice(0, 90);
      }
    }
    sendGaEvent({
      ...GA_EVENTS.REGISTRATION,
      action: `${step} | ${type}`,
      label: auxLabel,
      nonInteraction,
    });
  };

  useEffect(() => {
    const hasOpenSession = Boolean(sessionUser?.id);
    const isComplete = sessionUser?.level === USER_LEVEL.COMPLETE;
    if (isComplete || (hasOpenSession && !newClientEnabled)) {
      navigate(ROUTE_PATH.HOME, { replace: true });
      return;
    }
    if (hasOpenSession && !isComplete) {
      return;
    }
    // id y token de /signup/complete solo viven en memoria. Un refresh los
    // pierde; no hay otro endpoint. La recuperación es la de contraseña ya existente.
    const refresh = resolveRegistrationRefresh({
      isKnownStep: Object.values(STEPS).includes(step),
      isStart: step === STEPS.START,
      hasPhoneInMemory: Boolean(user?.phone),
      hasEmailPasswordToken: step === STEPS.PASSWORD && Boolean(window.mlsQueryParams?.token),
    });
    if (refresh === REGISTRATION_REFRESH.USE_EMAIL_PASSWORD_LINK) {
      setPasswordToken(window.mlsQueryParams?.token);
    } else if (refresh === REGISTRATION_REFRESH.RESTART_AND_RECOVER_PASSWORD) {
      navigate(ROUTE_PATH.setRegistration(STEPS.START), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isLegacyResume || LEGACY_RESUME_STEPS.includes(step)) {
      return;
    }
    navigate(ROUTE_PATH.setRegistration(STEPS.IDENTITY_VALIDATION), { replace: true });
  }, [isLegacyResume, step, navigate]);

  useEffect(() => {
    handleEvent(GA_REGISTRATION_ACTIONS.START);
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleBack = () => {
    switch (step) {
      case STEPS.START:
      case STEPS.BASIC_INFO:
      case STEPS.OTP_VALIDATION:
      case STEPS.EXISTS_OTP_VALIDATION:
      case STEPS.IDENTITY_RESUME:
      case STEPS.ADDRESS:
        navigate(-1);
        break;
      default:
        break;
    }
  };

  const handleContinue = (data) => {
    const onContinue = () => {
      handleEvent(GA_REGISTRATION_ACTIONS.COMPLETE);
    };

    const onError = (e) => {
      handleEvent(GA_REGISTRATION_ACTIONS.ERROR, e);
      let auxError = e;
      try {
        const errorString = JSON.stringify(e);
        let overwriteError = null;
        if (errorString.includes('Celular ya existe')) {
          overwriteError = ERROR_MESSAGES.PHONE_ALREADY_EXISTS_IN_SAP;
        } else if (errorString.includes('Correo ya existe')) {
          overwriteError = ERROR_MESSAGES.EMAIL_ALREADY_EXISTS_IN_SAP;
        } else if (errorString.includes('El RFC a registrar esta asociado a otro BP')) {
          overwriteError = ERROR_MESSAGES.RFC_ALREADY_EXISTS;
        }
        if (overwriteError) {
          auxError = { data: { error: overwriteError } };
        }
      } catch (ev) {
        /* */
      }
      setError(auxError);
    };

    const submitCompleteSignup = (addressData, otpData) => {
      const decision = resolveNewClientAuthStep({
        signupAuth,
        passwordSaved: newClientPasswordSaved,
      });
      if (!decision.callsComplete) {
        navigate(ROUTE_PATH.setRegistration(STEPS.PASSWORD), { replace: true });
        return;
      }
      const payload = buildCompleteSignupPayload({
        user,
        otp: otpData,
        userExtra,
        address: addressData || {},
        phone: `${PHONE_COUNTRY_CODE}${user.phone}`,
      });
      dispatch(postCompleteSignup(payload)).then((response) => {
        setSignupAuth({
          id: response?.id,
          token: response?.token,
        });
        setPendingCompleteRetry(false);
        setDuplicateAccount(null);
        onContinue();
        navigate(ROUTE_PATH.setRegistration(STEPS.PASSWORD), { replace: true });
      }).catch((e) => {
        const code = e?.data?.error;
        setCollectedAddress(addressData);
        if (isCompleteOtpRetryError(code)) {
          setPendingCompleteRetry(true);
          navigate(ROUTE_PATH.setRegistration(STEPS.OTP_VALIDATION));
        }
        if (DUPLICATE_ACCOUNT_ERRORS.includes(code)) {
          setDuplicateAccount({
            email: user.email,
            phone: user.phone,
            code,
          });
        }
        sendSentryLog({
          error: e,
          data: {
            e: user.email,
            p: user.phone,
          },
        }, 'Onboarding: complete signup error');
        onError(e);
      });
    };

    const refreshLegacySession = () => (
      dispatch(validateSession(false, true)).then((refreshed) => {
        if (!refreshed) {
          onError({ data: { error: 'ERROR_LOADING_DATA' } });
          return;
        }
        onContinue();
        navigate(ROUTE_PATH.setRegistration(STEPS.SUCCESS_COMPLETE), { replace: true });
      })
    );

    const submitLegacyComplete = (addressData) => {
      // El cierre ya respondió 200: un reintento solo refresca la sesión.
      if (legacyCompleteDone) {
        refreshLegacySession().catch((e) => {
          sendSentryLog({
            error: e,
            data: {
              u: sessionUser?.id,
              c: sessionUser?.clientId,
            },
          }, 'Onboarding: legacy session refresh error');
          onError(e);
        });
        return;
      }
      setClientOnboardingFieldErrors({});
      const payload = buildCompleteClientOnboardingPayload({
        userExtra,
        address: addressData || {},
      });
      dispatch(completeClientOnboarding(payload)).then(() => {
        setLegacyCompleteDone(true);
        return refreshLegacySession();
      }).catch((e) => {
        const parsed = readCompleteOnboardingFieldErrors(e);
        sendSentryLog({
          error: e,
          data: {
            u: sessionUser?.id,
            c: sessionUser?.clientId,
          },
        }, 'Onboarding: complete client onboarding error');
        if (parsed) {
          setClientOnboardingFieldErrors(parsed.fields);
          const parts = Object.values(parsed.fields).filter(Boolean);
          if (parsed.hasUnknown || parts.length === 0) {
            parts.push(errorLiterals.ONBOARDING_VALIDATION);
          }
          onError(parts.join('. '));
          return;
        }
        onError(e);
      });
    };

    switch (step) {
      case STEPS.START:
        if (data) {
          handleEvent(
            GA_REGISTRATION_ACTIONS.EVENT,
            exists.type === CLIENT_EXISTS_TYPES.INE
              ? GA_REGISTRATION_LABELS.START_SUBMIT_INE
              : GA_REGISTRATION_LABELS.START_SUBMIT_CLIENT_ID,
          );
          dispatch(searchClient(exists.type, exists.value)).then((response) => {
            onContinue();
            setExists((prev) => ({
              ...prev,
              email: response?.email || '',
              phone: response?.phone || '',
            }));
            navigate(ROUTE_PATH.setRegistration(STEPS.EXISTS_OTP_VALIDATION));
          }).catch((e) => {
            switch (e.statusCode) {
              case 406:
                e.data.error = ERROR_MESSAGES.USER_ALREADY_EXISTS;
                break;
              case 409:
                e.data.error = literals.start.errors.incompleteData;
                break;
              case 500:
                e.data.error = literals.start.errors.serviceUnavailable;
                break;
              default:
                e.data.error = literals.start.errors.userNotFound;
            }
            onError(e);
          });
        } else {
          onContinue();
          setExists((prev) => ({
            ...prev,
            email: '',
            phone: '',
          }));
          navigate(ROUTE_PATH.setRegistration(STEPS.BASIC_INFO));
        }
        break;
      case STEPS.BASIC_INFO:
        dispatch(sendSignupOTP(
          user.email,
          `${PHONE_COUNTRY_CODE}${user.phone}`,
          otpChannel,
        )).then(() => {
          setOtpLocked({ phone: false, email: false });
          onContinue();
          navigate(ROUTE_PATH.setRegistration(STEPS.OTP_VALIDATION));
        }).catch((e) => {
          if (typeof e?.data?.error === 'string' && literals.basicInfo?.errors?.[e?.data?.error]) {
            e.data.error = literals.basicInfo.errors[e?.data?.error];
          }
          onError(e);
          sendSentryLog({
            error: e,
            data: {
              n: `${user.firstName} ${user.paternal}`,
              e: user.email,
              p: user.phone,
            },
          }, 'Onboarding: basic signup send otp error');
        });
        break;
      case STEPS.EXISTS_OTP_VALIDATION:
        dispatch(verifyClientAccountOTP(exists.value, data)).then((response) => {
          setPasswordToken(response.token);
          setUser((prev) => ({
            ...prev,
            clientId: response?.client_id || response?.clientId || '',
          }));
          setExists((prev) => ({
            ...prev,
            linked: true,
          }));
          onContinue();
          navigate(ROUTE_PATH.setRegistration(STEPS.PASSWORD), { replace: true });
        }).catch((e) => {
          try {
            if (e?.statusCode === 401) {
              e.data.error = literals.existsOTPValidation.errors.invalidOTP;
            }
          } catch (ev) {
            /* */
          }
          sendSentryLog({
            error: e,
            data: { ...exists },
          }, 'Onboarding: Exists otp validation error');
          onError(e);
        });
        break;
      case STEPS.OTP_VALIDATION: {
        // Un código bloqueado no se revalida: el usuario tiene que reenviar.
        if (otpLocked.phone || otpLocked.email) {
          onError({
            data: {
              error: otpLocked.phone ? 'OTP_PHONE_LOCKED' : 'OTP_EMAIL_LOCKED',
            },
          });
          break;
        }
        if (pendingCompleteRetry) {
          setOtp(data);
          submitCompleteSignup(collectedAddress, data);
          break;
        }
        // El canal de envío no entra en la verificación.
        dispatch(verifySignupOTP({
          phone: `${PHONE_COUNTRY_CODE}${user.phone}`,
          otp_phone: data.phone,
          email: user.email,
          otp_email: data.email,
        })).then(() => {
          setOtp(data);
          onContinue();
          navigate(ROUTE_PATH.setRegistration(STEPS.IDENTITY_VALIDATION));
        }).catch((e) => {
          const code = e?.data?.error;
          const field = getSignupOtpFieldToReset(code);
          if (field) {
            setOtpReset({ field, id: Date.now() });
          }
          if (isSignupOtpLockedError(code) && field) {
            setOtpLocked((prev) => ({ ...prev, [field]: true }));
          }
          const message = mapSignupOtpVerifyMessage(e, errorLiterals);
          const translated = errorLiterals[code] || code;
          onError(message && message !== translated ? message : e);
          sendSentryLog({
            error: e,
            data: {
              e: user.email,
              p: user.phone,
            },
          }, 'Onboarding: signup otp verify error');
        });
        break;
      }
      case STEPS.PASSWORD: {
        const authDecision = resolveNewClientAuthStep({
          signupAuth,
          passwordSaved: newClientPasswordSaved,
        });
        if (!authDecision.callsComplete) {
          // El token del alta solo autoriza /users/password.
          // La sesión se abre con correo y contraseña.
          const signInNewClient = () => {
            dispatch(onLogin({
              email: user.email,
              password: data.password,
              signInType: LOGIN_TYPES.EMAIL,
            })).then((response) => {
              if (!response) {
                onError({ data: { error: 'unknownUserOrPassword' } });
                return;
              }
              onContinue();
              const storedUser = {
                ...userData,
                id: response?.id,
                lastUserName: response?.firstName || user.firstName || '',
                lastLogin: new Date().getTime(),
                email: response?.email || user.email,
                clientId: response?.clientId,
              };
              setUserData(storedUser);
              const goSuccess = () => {
                navigate(ROUTE_PATH.setRegistration(STEPS.SUCCESS_COMPLETE), { replace: true });
              };
              const enableBiometric = () => {
                if (data.biometric && response?.id) {
                  generateSessionToken({ id: response.id }, (success) => {
                    if (success) {
                      setUserData({ ...storedUser, biometric: true });
                    }
                    goSuccess();
                  });
                  return;
                }
                goSuccess();
              };
              if (getIdMissionLogTiming() === ID_MISSION_LOG_TIMING.AFTER_AUTH) {
                createIdMissionLog(buildIdMissionLogPayload(userExtra)).finally(enableBiometric);
              } else {
                enableBiometric();
              }
            }).catch((e) => {
              onError(e);
            });
          };

          if (authDecision.step === NEW_CLIENT_AUTH_STEP.SIGN_IN) {
            signInNewClient();
            break;
          }

          dispatch(savePassword(authDecision.token, data.password))
            .then(() => {
              setNewClientPasswordSaved(true);
              signInNewClient();
            })
            .catch((e) => {
              onError(e);
            });
          break;
        }

        // Cliente existente o enlace de correo: conservan /users/signin.
        const fromEmailLink = !user.email;

        const onSavePassword = () => {
          onContinue();
          if (!fromEmailLink) {
            navigate(ROUTE_PATH.setRegistration(exists?.linked ? STEPS.SUCCESS_BASIC_LINKED : STEPS.SUCCESS_BASIC), { replace: true });
          }
          const loginData = {
            email: user.email || data.email || '',
            idClient: user.clientId || '',
            password: data.password,
            signInType: user.clientId ? LOGIN_TYPES.CLIENT_ID : LOGIN_TYPES.EMAIL,
            welcome_email: true,
          };
          dispatch(onLogin(loginData)).then((response) => {
            if (response) {
              setUserData({
                ...userData,
                id: response?.id,
                lastUserName: response?.firstName || '',
                lastLogin: new Date().getTime(),
                email: response?.email,
                clientId: response?.clientId,
              });
              setUser({
                ...user,
                firstName: user.firstName || response.firstName || '',
                lastName: user.lastName || response.lastName || '',
                email: user.email || data.email || '',
                phone: user.phone || response.phone || '',
                clientId: response?.clientId || '-',
              });
              if (fromEmailLink) {
                navigate(ROUTE_PATH.setRegistration(STEPS.SUCCESS_BASIC), { replace: true });
              }
              if (data.biometric) {
                generateSessionToken({ id: response.id }, (success) => {
                  if (success) {
                    setUserData((prev) => ({ ...prev, biometric: true }));
                  }
                });
              }
            } else if (fromEmailLink) {
              setErrorEmailPassword(true);
              onError({ data: { error: literals.password.passwordSavedButInvalidEmail } });
            }
          });
        };

        if (errorEmailPassword) {
          onSavePassword();
        } else {
          dispatch(savePassword(passwordToken, data.password))
            .then(onSavePassword)
            .catch((e) => {
              if (fromEmailLink && e.statusCode === 401) {
                e.data.error = ERROR_MESSAGES.INVALID_PASSWORD_TOKEN;
              }
              onError(e);
            });
        }
        break;
      }
      case STEPS.SUCCESS_BASIC:
      case STEPS.SUCCESS_BASIC_LINKED:
        onContinue();
        navigate(ROUTE_PATH.HOME, { replace: true });
        break;
      case STEPS.IDENTITY_VALIDATION:
        setUserExtra({
          ...userExtra,
          ...data,
        });
        onContinue();
        navigate(ROUTE_PATH.setRegistration(STEPS.IDENTITY_RESUME));
        break;
      case STEPS.IDENTITY_RESUME:
        onContinue();
        navigate(ROUTE_PATH.setRegistration(STEPS.ADDRESS));
        break;
      case STEPS.ADDRESS:
        setCollectedAddress(data);
        if (isLegacyResume) {
          submitLegacyComplete(data);
          break;
        }
        if (
          duplicateAccount
          && duplicateAccount.email === user.email
          && duplicateAccount.phone === user.phone
        ) {
          onError({ data: { error: duplicateAccount.code } });
          break;
        }
        if (pendingCompleteRetry) {
          navigate(ROUTE_PATH.setRegistration(STEPS.OTP_VALIDATION));
          break;
        }
        submitCompleteSignup(data, otp);
        break;
      case STEPS.SUCCESS_COMPLETE:
        onContinue();
        navigate(ROUTE_PATH.HOME, { replace: true });
        break;
      default:
        onContinue();
        navigate(ROUTE_PATH.setRegistration(step));
    }
  };

  const handleResend = (type) => {
    const isEmail = type === 'email';
    const field = isEmail ? 'email' : 'phone';
    return dispatch(sendSignupOTP(
      isEmail ? user.email : '',
      !isEmail ? `${PHONE_COUNTRY_CODE}${user.phone}` : '',
      !isEmail ? otpChannel : undefined,
    )).then((response) => {
      // Un envío nuevo reinicia el contador y habilita otra validación.
      setOtpLocked((prev) => ({ ...prev, [field]: false }));
      setOtpReset({ field, id: Date.now() });
      return response;
    });
  };

  const retryOtpBySms = () => {
    const phone = `${PHONE_COUNTRY_CODE}${user.phone}`;
    const alreadyOnOtp = step === STEPS.OTP_VALIDATION;
    setOtpChannel(SIGNUP_OTP_CHANNEL.SMS);
    const request = alreadyOnOtp
      ? dispatch(sendSignupOTP('', phone, SIGNUP_OTP_CHANNEL.SMS))
      : dispatch(sendSignupOTP(user.email, phone, SIGNUP_OTP_CHANNEL.SMS));
    request.then(() => {
      setSmsFallbackSent(true);
      setError(null);
      setOtpLocked((prev) => ({ ...prev, phone: false }));
      if (alreadyOnOtp) {
        setOtpReset({ field: 'phone', id: Date.now() });
      } else {
        navigate(ROUTE_PATH.setRegistration(STEPS.OTP_VALIDATION));
      }
    }).catch((e) => {
      handleEvent(GA_REGISTRATION_ACTIONS.ERROR, e);
      setError(e);
    });
  };

  const handleRequestOtp = (type) => {
    return dispatch(getUserOtp(exists.value, type)).then(() => {
      return true;
    }).catch((e) => {
      handleEvent(GA_REGISTRATION_ACTIONS.ERROR, e);
      setError(e);
      return false;
    });
  };

  const existingClientProgress = !newClientEnabled
    || step === STEPS.EXISTS_OTP_VALIDATION
    || (Boolean(exists.linked) && !signupAuth?.token)
    || (step === STEPS.PASSWORD && !signupAuth?.token);
  const progress = getRegistrationProgress(step, existingClientProgress);

  const props = {
    literals,
    onContinue: handleContinue,
    onBack: handleBack,
    onEvent: handleEvent,
    headerStep: progress.step,
    headerTotal: progress.total,
    onError: (e) => {
      handleEvent(GA_REGISTRATION_ACTIONS.ERROR, e);
      setError(e);
    },
  };

  const successStep = [STEPS.SUCCESS_BASIC, STEPS.SUCCESS_BASIC_LINKED, STEPS.SUCCESS_COMPLETE].includes(step);
  const errorCode = typeof error?.data?.error === 'string' ? error.data.error : '';
  let errorActions = null;
  if (errorCode === 'CLIENT_NOT_FOUND') {
    errorActions = (
      <>
        <Button className='mt-6' onClick={() => setError(null)}>{literals.common.close}</Button>
        <Button className='mt-2' type='outline-primary' onClick={openSupport}>
          {literals.contactSupport}
        </Button>
      </>
    );
  } else if (errorCode === ERROR_MESSAGES.ERROR_SENDING_WHATSAPP) {
    errorActions = (
      <>
        <Button className='mt-6' onClick={retryOtpBySms}>{literals.OTPValidation.sendBySms}</Button>
        <Button type='outline-primary' className='mt-2' onClick={() => setError(null)}>
          {literals.common.close}
        </Button>
      </>
    );
  } else if (DUPLICATE_ACCOUNT_ERRORS.includes(errorCode)) {
    errorActions = (
      <>
        <Button className='mt-6' to={ROUTE_PATH.LOGIN}>{literals.goToLogin}</Button>
        <Button className='mt-2' type='outline-primary' to={ROUTE_PATH.RECOVER_PASSWORD}>
          {literals.goToRecoverPassword}
        </Button>
      </>
    );
  } else if (errorCode && ERROR_MESSAGES[errorCode]) {
    errorActions = (
      <>
        {[ERROR_MESSAGES.EMAIL_ALREADY_EXISTS_IN_SAP, ERROR_MESSAGES.PHONE_ALREADY_EXISTS_IN_SAP].includes(errorCode) ? (
          <Button
            onClick={() => {
              navigate(ROUTE_PATH.setRegistration(STEPS.START));
              setError(null);
            }}
            className='mt-6'
          >
            {literals.goToLinkAccount}
          </Button>
        ) : (
          <>
            {errorCode === ERROR_MESSAGES.INVALID_PASSWORD_TOKEN ? (
              <Button to={ROUTE_PATH.RECOVER_PASSWORD} className='mt-6'>{literals.goToRecoverPassword}</Button>
            ) : (
              <Button to={ROUTE_PATH.LOGIN} className='mt-6'>{literals.goToLogin}</Button>
            )}
          </>
        )}
        <Button
          type='outline-primary'
          className='mt-2'
          onClick={() => setError(null)}
        >
          {literals.common.close}
        </Button>
      </>
    );
  }

  // When the gate is off nothing renders: the effect above already
  // redirects to Home.
  if (!registrationEnabled) return null;

  return (
    <Layout className='new-client-layout' bgWhite={!successStep} headerMenu={successStep} rightMenu={false}>
      <AppShell variant='narrow'>
        {step === STEPS.START && (
          <ClientStart
            exists={exists}
            onChange={setExists}
            showNewRegistration={newClientEnabled}
            {...props}
          />
        )}
        {step === STEPS.BASIC_INFO && (
          <ClientBasicInfo
            user={user}
            onChange={setUser}
            {...props}
          />
        )}
        {step === STEPS.EXISTS_OTP_VALIDATION && (
          <ClientExistsOTPValidation
            exists={exists}
            onRequestOtp={handleRequestOtp}
            {...props}
          />
        )}
        {step === STEPS.OTP_VALIDATION && (
          <ClientOTPValidation
            user={user}
            onResend={handleResend}
            channel={otpChannel}
            refreshingOtp={pendingCompleteRetry}
            smsFallbackSent={smsFallbackSent}
            otpReset={otpReset}
            onCloseSmsFallback={() => setSmsFallbackSent(false)}
            {...props}
          />
        )}
        {step === STEPS.PASSWORD && (<ClientPassword customer={user} errorEmail={errorEmailPassword} {...props} />)}
        {step === STEPS.SUCCESS_BASIC && (
          <ClientSuccessMessage type={SUCCESS_TYPES.BASIC} customer={user} deferCompletion {...props} />
        )}
        {step === STEPS.SUCCESS_BASIC_LINKED && (
          <ClientSuccessMessage type={SUCCESS_TYPES.BASIC_LINKED} customer={user} deferCompletion {...props} />
        )}
        {step === STEPS.IDENTITY_VALIDATION && (
          <ClientIdentityValidation
            {...props}
            logOnCapture={isLegacyResume}
          />
        )}
        {step === STEPS.IDENTITY_RESUME && (
          <ClientIdentityResume
            data={userExtra}
            onChange={setUserExtra}
            fieldErrors={clientOnboardingFieldErrors}
            {...props}
          />
        )}
        {step === STEPS.ADDRESS && (
          <ClientAddress
            data={userExtra}
            fieldErrors={clientOnboardingFieldErrors}
            {...props}
          />
        )}
        {step === STEPS.SUCCESS_COMPLETE && <ClientSuccessMessage type={SUCCESS_TYPES.COMPLETE} customer={sessionUser} {...props} />}
        {error && (
          <ErrorModal description={error} onClose={() => setError(null)}>
            {errorActions}
          </ErrorModal>
        )}
      </AppShell>
    </Layout>
  );
};

export default Registration;
